import { useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "@shared/api";
import type { FollowedThread, NotifyLevel, Timestamp, Uuid } from "@shared/api";
import { dayTime, time } from "@shared/lib/format";
import { messageLink } from "@shared/lib/jump";
import { Menu, MenuOn, SEPARATOR, type MenuItem } from "@shared/ui/Menu";
import { Dialog } from "@shared/ui/bits";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Permission, has, useChannelRights } from "@entities/role";
import { useSession } from "@entities/session";
import { useStore } from "../model/store";
import { TITLE_MAX, ThreadSettings } from "./thread-settings";

/** Ветка, на которой стоит меню: всё, чем оно распоряжается. */
export interface ThreadFacts {
  channelId: Uuid;
  threadId: Uuid;
  /** Корень: по нему открывается панель. `null` — корень удалён. */
  rootId: Uuid | null;
  /**
   * Хозяин ветки — кто её завёл, как в Discord, а не автор корня: ему, как и
   * тем, кто управляет сообщениями, можно править и закрывать ветку.
   */
  ownerId?: Uuid | null;
  followed: boolean;
  notifyLevel: NotifyLevel | null;
  /** Заглушена на срок — до какого времени. */
  notifyUntil?: Timestamp | null;
  /** Имя ветки — шапка окна «Редактировать ветку» и подпись удаления. */
  name?: string | null;
  closed?: boolean;
  locked?: boolean;
  /** Новых ответов — «Пометить как прочитанное» без них серое, как в Discord. */
  unread?: number;
}

/** «Заглушить ветку» — сроки Discord; `null` — пока не включу. */
const MUTE_FOR: { label: string; minutes: number | null }[] = [
  { label: "На 15 минут", minutes: 15 },
  { label: "На 1 час", minutes: 60 },
  { label: "На 3 часа", minutes: 180 },
  { label: "На 8 часов", minutes: 480 },
  { label: "На 24 часа", minutes: 1440 },
  { label: "Пока не включу", minutes: null },
];

/** Уровни Discord. «Как у канала» нет: у ветки без своего уровня отмечен тот, что действует. */
const LEVELS: { level: NotifyLevel; label: string }[] = [
  { level: "all", label: "Все сообщения" },
  { level: "mentions", label: "Только @упоминания" },
  { level: "none", label: "Ничего" },
];

/** «до 18:30» сегодня, «до 30 октября, 9:00» — позже. */
function mutedLabel(until: string): string {
  const at = new Date(until);
  return at.toDateString() === new Date().toDateString() ? `до ${time(until)}` : `до ${dayTime(until)}`;
}

/** Разделители без пунктов между ними уходят: меню собирают с пропусками по правам. */
function compact(items: (MenuItem | false | null | undefined)[]): MenuItem[] {
  const result: MenuItem[] = [];
  for (const item of items) {
    if (!item) continue;
    if (item === SEPARATOR && (result.length === 0 || result[result.length - 1] === SEPARATOR)) continue;
    result.push(item);
  }
  if (result[result.length - 1] === SEPARATOR) result.pop();
  return result;
}

/** Где открыто меню: строкой ветки (правая кнопка) или «…» в шапке открытой ветки. */
export type ThreadMenuPlace =
  | { kind: "row" }
  | {
      kind: "head";
      /**
       * Ветка открыта на весь экран. Меню тогда короче, как в Discord:
       * уведомления, закреплённые и поиск стоят значками в шапке.
       */
      full: boolean;
      onSearch: () => void;
      onPins: () => void;
      /** «Наверх» — к началу ветки на весь экран. */
      onTop: () => void;
    };

/**
 * Действия над веткой — как в Discord, в его порядке.
 *
 * Одни на все места, где ветка встречается: строка под сообщением, строка в
 * списке веток, ветка под каналом в списке каналов и «…» в шапке открытой
 * ветки. Разные меню на один объект — это когда «покинуть» есть в списке и
 * нет в ленте, и человек начинает искать, где же оно было.
 *
 * Чего нет по праву — того нет вовсе: закрыть и править ветку может её
 * хозяин (кто её завёл) или тот, кто управляет сообщениями, блокировать и удалять — только
 * второй, как «Управлять ветками» в Discord. Права спрашиваются у канала
 * ветки, а не у открытого: из списка каналов меню зовут на чужой канал.
 */
export function useThreadActions(thread: ThreadFacts, place: ThreadMenuPlace) {
  const store = useStore();
  const queryClient = useQueryClient();
  const me = useSession((state) => state.me);
  const rights = useChannelRights(thread.channelId);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const manage = has(rights?.permissions ?? 0, Permission.ManageThreads);
  const canEdit = manage || (!!me && !!thread.ownerId && thread.ownerId === me.id);
  const { channelId, threadId } = thread;

  /** Ветка поменялась на сервере — перечитать везде, где она видна. */
  const refresh = () => {
    store.loadThreads(channelId);
    store.reloadThread(channelId, threadId);
    void queryClient.invalidateQueries({ queryKey: ["followed-threads"] });
  };

  /*
   * Отметка ставится по последнему ответу, и его приходится спросить: сервер
   * хранит сообщение, а не время, а строка ветки знает только счёт.
   */
  async function markAllRead() {
    const last = await api.threads.messages(channelId, threadId, { limit: 1 });
    const newest = last[last.length - 1];
    if (newest) await store.markThreadRead(channelId, threadId, newest.id);
  }

  function mute(minutes: number | null) {
    if (minutes === null) void store.setThreadNotify(channelId, threadId, "none");
    else void api.threads.setNotify(channelId, threadId, "none", { minutes }).then(refresh, () => {});
  }

  function unmute() {
    // Заглушённая на срок возвращается к прежнему своему уровню, насовсем — к уровню канала.
    if (thread.notifyUntil) void api.threads.setNotify(channelId, threadId, null, { unmute: true }).then(refresh, () => {});
    else void store.setThreadNotify(channelId, threadId, null);
  }

  /*
   * Не в ветке — уведомлений у неё для вас нет, и пункты серые, как в
   * Discord: сначала «Присоединиться к ветке».
   */
  const muteItem: MenuItem = !thread.followed
    ? { label: "Заглушить ветку" }
    : thread.notifyLevel === "none"
      ? {
          label: "Включить уведомления",
          hint: thread.notifyUntil ? mutedLabel(thread.notifyUntil) : undefined,
          onPick: unmute,
        }
      : {
          label: "Заглушить ветку",
          items: MUTE_FOR.map((item) => ({ label: item.label, onPick: () => mute(item.minutes) })),
        };

  const level = thread.notifyLevel ?? store.levelIn(channelId);
  const levelItems: MenuItem[] = LEVELS.map((item) => ({
    label: item.label,
    radio: true,
    on: thread.followed && level === item.level,
    onPick: thread.followed ? () => void store.setThreadNotify(channelId, threadId, item.level) : undefined,
  }));
  const notifyItem: MenuItem = !thread.followed
    ? { label: "Параметры уведомлений" }
    : { label: "Параметры уведомлений", items: levelItems };

  const closeItem: MenuItem | false = canEdit && {
    label: thread.closed ? "Открыть ветку" : "Закрыть ветку",
    onPick: () => void api.threads.close(channelId, threadId, !thread.closed).then(refresh, () => {}),
  };
  const lockItem: MenuItem | false = manage && {
    label: thread.locked ? "Разблокировать ветку" : "Заблокировать ветку",
    onPick: () => void api.messages.lockThread(channelId, threadId, !thread.locked).then(refresh, () => {}),
  };
  const editItem: MenuItem | false = canEdit && { label: "Редактировать ветку", onPick: () => setEditing(true) };
  const deleteItem: MenuItem | false = manage && {
    label: "Удалить ветку",
    kind: "danger",
    onPick: () => setDeleting(true),
  };
  const linkItem: MenuItem = {
    label: "Копировать ссылку",
    onPick: () =>
      void navigator.clipboard
        ?.writeText(messageLink({ serverId: store.serverOf(channelId) ?? null, channelId, messageId: null, threadId }))
        .catch(() => {}),
  };
  /*
   * Под каналом в списке стоят подписанные ветки, а их список знает только
   * открытую ветку. Покинутая уходит оттуда сразу, не дожидаясь ответа, — как
   * в Discord; потом список переспрашивается в любом случае.
   */
  function follow(on: boolean) {
    if (!on)
      queryClient.setQueriesData<FollowedThread[]>({ queryKey: ["followed-threads"] }, (items) =>
        items?.filter((item) => item.id !== threadId),
      );
    void store
      .followThread(channelId, threadId, on)
      .finally(() => void queryClient.invalidateQueries({ queryKey: ["followed-threads"] }));
  }
  const followItem: MenuItem = thread.followed
    ? { label: "Покинуть ветку", onPick: () => follow(false) }
    : { label: "Присоединиться к ветке", onPick: () => follow(true) };
  /** «Присоединиться» в шапке ветки — первым пунктом, «Покинуть» — внизу, как в Discord. */
  const joinFirst = !thread.followed && followItem;
  const leaveLast = thread.followed && followItem;

  const items =
    place.kind === "row"
      ? compact([
          {
            label: "Пометить как прочитанное",
            onPick: thread.unread === 0 ? undefined : () => void markAllRead(),
          },
          SEPARATOR,
          { label: "Добавить в избранное", kind: "plan" },
          SEPARATOR,
          closeItem,
          lockItem,
          editItem,
          linkItem,
          SEPARATOR,
          muteItem,
          notifyItem,
          SEPARATOR,
          deleteItem,
          SEPARATOR,
          followItem,
        ])
      : place.full
        ? compact([
            joinFirst,
            { label: "Наверх", onPick: place.onTop },
            editItem,
            { label: "Открыть раздельно", onPick: () => store.expandThread(false) },
            SEPARATOR,
            closeItem,
            lockItem,
            deleteItem,
            SEPARATOR,
            leaveLast,
            SEPARATOR,
            linkItem,
          ])
        : compact([
            joinFirst,
            {
              label: "Открыть на весь экран",
              onPick: () => {
                if (store.panel?.threadId !== threadId && thread.rootId) store.openThread(channelId, thread.rootId, threadId);
                store.expandThread(true);
              },
            },
            SEPARATOR,
            muteItem,
            notifyItem,
            SEPARATOR,
            editItem,
            SEPARATOR,
            { label: "Поиск", onPick: place.onSearch },
            { label: "Закреплённые", onPick: place.onPins },
            SEPARATOR,
            closeItem,
            lockItem,
            deleteItem,
            SEPARATOR,
            leaveLast,
            SEPARATOR,
            linkItem,
        ]);

  const dialogs = (
    <>
      {editing && (
        <ThreadSettings
          channelId={channelId}
          threadId={threadId}
          canManage={manage}
          canDelete={manage}
          onChanged={refresh}
          onDelete={() => {
            setEditing(false);
            setDeleting(true);
          }}
          onClose={() => setEditing(false)}
        />
      )}
      {deleting && (
        <DeleteThread
          channelId={channelId}
          threadId={threadId}
          name={thread.name ?? null}
          onDone={() => {
            setDeleting(false);
            if (store.panel?.threadId === threadId) store.closePanel();
            refresh();
          }}
          onClose={() => setDeleting(false)}
        />
      )}
    </>
  );

  /**
   * Колокольчик в шапке ветки — как в Discord: «Заглушить ветку», уровни
   * кружками прямо в меню и «Покинуть ветку» внизу.
   */
  const bell = compact([joinFirst, SEPARATOR, muteItem, SEPARATOR, ...levelItems, SEPARATOR, leaveLast]);

  return { items, bell, dialogs };
}

/**
 * Меню ветки по правой кнопке — обёрткой вокруг строки, на которой его зовут.
 * Меню носит на себе сама строка: экрану не нужно помнить, по чему ткнули.
 */
export function ThreadMenuOn({ thread, children }: { thread: ThreadFacts; children: ReactNode }) {
  const { items, dialogs } = useThreadActions(thread, { kind: "row" });

  return (
    <>
      <MenuOn title="ВЕТКА" sub={thread.name ?? undefined} items={items}>
        {children}
      </MenuOn>
      {dialogs}
    </>
  );
}

/** Меню у кнопки шапки — «…» или колокольчик; точка — угол кнопки. */
export function ThreadHeadMenu({
  title,
  at,
  items,
  onClose,
}: {
  title?: string;
  at: { x: number; y: number };
  items: MenuItem[];
  onClose: () => void;
}) {
  return <Menu title={title} at={at} items={items} onClose={onClose} />;
}

/**
 * «Удалить ветку» — как в Discord: спрашивает, потому что вернуть нельзя.
 * Уходят ответы, корень остаётся в канале.
 */
function DeleteThread({
  channelId,
  threadId,
  name,
  onDone,
  onClose,
}: {
  channelId: Uuid;
  threadId: Uuid;
  name: string | null;
  onDone: () => void;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function remove() {
    setBusy(true);
    setProblem(null);
    try {
      await api.threads.remove(channelId, threadId);
      onDone();
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "Ветка не удалилась");
      setBusy(false);
    }
  }

  return (
    <Dialog
      title="Удалить ветку"
      onClose={onClose}
      actions={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button type="button" variant="destructive" disabled={busy} onClick={() => void remove()}>
            Удалить
          </Button>
        </>
      }
    >
      <p className="small">
        {name ? `Ветка «${name}» удалится` : "Ветка удалится"} вместе со всеми ответами. Сообщение, с которого
        она началась, останется в канале. Вернуть ветку нельзя.
      </p>
      {problem && <div className="small danger-text">{problem}</div>}
    </Dialog>
  );
}

/**
 * «Изменить заголовок» поста форума: без заголовка поста нет. У обычной ветки
 * имя правят в «Редактировать ветку».
 */
export function RenameThread({
  channelId,
  threadId,
  name,
  required = false,
  onDone,
  onClose,
}: {
  channelId: Uuid;
  threadId: Uuid;
  name: string | null;
  required?: boolean;
  onDone: () => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(name ?? "");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setProblem(null);
    try {
      await api.threads.title(channelId, threadId, title.trim() || null);
      onDone();
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "Не переименовалась");
      setBusy(false);
    }
  }

  return (
    <Dialog
      title={required ? "Изменить заголовок" : "Переименовать ветку"}
      onClose={onClose}
      actions={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button type="button" disabled={busy || (required && !title.trim())} onClick={() => void save()}>
            Сохранить
          </Button>
        </>
      }
    >
      <Input
        value={title}
        maxLength={TITLE_MAX}
        placeholder={required ? "Заголовок поста" : "Пусто — звать началом сообщения"}
        aria-label={required ? "Заголовок поста" : "Имя ветки"}
        onChange={(event) => setTitle(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !(required && !title.trim())) {
            event.preventDefault();
            void save();
          }
        }}
      />
      {problem && <div className="small danger-text">{problem}</div>}
    </Dialog>
  );
}
